import { useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Space, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import type { ChannelListColumns, UseChannelList } from '../ChannelList.types';
import {
  useGetChannelListQuery,
  useUpdateChannelMutation,
  useGetDspOptionsQuery
} from '@/api';
import { COLUMNS, Column } from '../ChannelList.constants';
import { usePagination } from '@/hooks/usePagination';
import { useSorting } from '@/hooks/useSorting';
import { Tag } from '@/components';
import { DATE_FORMAT, NAMESPACE } from '@/constants';
import { formatMoney } from '@/constants/currency';
import { isMeasurementChannel } from '../../../utils/measurementChannel';
import {
  resolveDeliveredClicks,
  resolveDeliveredViews,
  shouldShowEstimatedClicks,
  shouldShowEstimatedListens,
  shouldShowEstimatedViews,
  shouldShowImpressions
} from '../../../utils/deliveryMetrics';
import { ChannelListItem } from '@/models';
import { formatNumber } from '@/utils';
import Actions from '../components';
import { EditableCell } from '../components/EditableCell';
import { formatMarginPercent, resolveGrossMarginPercent } from '../../../utils/marginDisplay';
import { resolveLineGrossBudget, resolveLineNetBudget } from '../../../utils/netBudget';
import { duplicateLineItem } from '../../../utils/lineItemFormMode';

const { PrimaryTag } = Tag;

const CELL_TEXT = { fontSize: 12, display: 'block', textAlign: 'center' as const };

const cellText = (content: ReactNode) => <span style={CELL_TEXT}>{content}</span>;

const coverWithTag = (value?: string[] | string) => {
  const data = Array.isArray(value) ? value : value ? [value] : [];

  if (data.length > 0) {
    /* Each tag is held to the column's width and wraps its own text: a long
       format ("Standard IAB & Interstitial") would otherwise run out of both
       sides of a centred cell and over the columns beside it. */
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4, fontSize: 12 }}>
        {data.map((item) => (
          <PrimaryTag
            key={item}
            style={{ maxWidth: '100%', marginInlineEnd: 0, whiteSpace: 'normal', textAlign: 'center' }}
          >
            {item}
          </PrimaryTag>
        ))}
      </div>
    );
  }
  return cellText('N/A');
};

const productAsList = (product?: string) => {
  if (!product) return cellText('N/A');
  const items = product.split(',').map((s) => s.trim()).filter(Boolean);
  if (items.length <= 1) return cellText(product);
  return (
    <ul style={{ margin: 0, paddingLeft: 14, fontSize: 12, textAlign: 'left' }}>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
};

const isDefined = (value: unknown): boolean => value !== undefined && value !== null;
const getDateFormat = (date?: Date): string => (date ? dayjs(date).format(DATE_FORMAT) : 'N/A');
const getCurrencyFormat = (value?: number, currency?: string | null): string =>
  isDefined(value) ? formatMoney(value as number, currency) : 'N/A';
const getNumberFormat = (value?: number): string =>
  value != null && value > 0 ? formatNumber(value, 0) : 'N/A';

const getPercentFormat = (value?: number): string => formatMarginPercent(value);

export const useChannelList: UseChannelList = ({
  mediaPlanId,
  planCurrency,
  agencyDiscount,
  isBooked,
  openChannelForm
}) => {
  const [updateChannel] = useUpdateChannelMutation();
  const { data: dspOptionsData } = useGetDspOptionsQuery();


  const { page, limit, handlePaginationChange } = usePagination({ page: 1, limit: 10 });
  const { order } = useSorting<string>({});
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);

  /**
   * Saves one field of one line item.
   *
   * Goes through the same PUT the form uses, so a cell edit runs the identical
   * server chain: metrics, batch margins, the plan refresh, and the booking
   * sync for a signed plan. Nothing about recalculation is reimplemented here —
   * an inline edit that computed its own margin would be a second source of
   * truth for the number people price from.
   */
  const saveField = useCallback(
    async (id: string, field: string, value: unknown) => {
      try {
        await updateChannel({ _id: id, [field]: value } as never).unwrap();
        message.success(t('lineItemUpdated'));
      } catch (error) {
        /* Surfaced, not swallowed. The cell has already returned to text, so a
           silent failure reads exactly like a saved value. */
        /* The API's own words where it gave any: a rejected edit usually says
           something specific (a budget overrun, a missing rate card) that is
           more use than a generic failure. updateChannel is excluded from the
           global error toast, so nothing else reports this. */
        const detail = (error as { data?: { message?: string } })?.data?.message;
        message.error(detail || t('lineItemUpdateFailed'));
        throw error;
      }
    },
    [updateChannel, t]
  );
  const { data: channels, isLoading } = useGetChannelListQuery({
    page,
    limit,
    order,
    mediaPlan: [mediaPlanId]
  });

  const columns = useMemo((): ChannelListColumns => {
    COLUMNS[Column.CHANNEL].render = (_, { channel }) => cellText(channel ?? 'N/A');
    COLUMNS[Column.FORMAT].render = (_, { formats }) => coverWithTag(formats);
    COLUMNS[Column.PRODUCT].render = (_, { product }) => productAsList(product);
    COLUMNS[Column.TARGETING_DETAILS].render = (_, { targetingDetails }) =>
      targetingDetails ? (
        <Tooltip title={targetingDetails}>{cellText(targetingDetails)}</Tooltip>
      ) : (
        cellText('N/A')
      );
    COLUMNS[Column.TARGETING_KPI].render = (_, { kpi }) => cellText(kpi ?? 'N/A');
    COLUMNS[Column.RATE_SOURCE].render = (_, { rateSource }) => (
      <Space style={{ justifyContent: 'center', width: '100%' }}>
        <PrimaryTag>
          {rateSource === 'PACKAGE'
            ? t('package')
            : rateSource === 'TECH_FEE'
              ? t('techFee')
              : t('rateCard')}
        </PrimaryTag>
      </Space>
    );
    COLUMNS[Column.BUYING_METRIC].render = (_, { buyingMetric }) => cellText(buyingMetric ?? 'N/A');
    COLUMNS[Column.COUNTRY].render = (_, { country }) => cellText(country ?? 'N/A');
    COLUMNS[Column.CURRENCY].render = (_, { currency }) => cellText(currency ?? 'N/A');
    COLUMNS[Column.DSP].render = (_, { dsp, channel: rowChannel, rateSource }) =>
      cellText(
        isMeasurementChannel(rowChannel) || rateSource === 'PACKAGE' || rateSource === 'TECH_FEE'
          ? 'N/A'
          : (dsp ?? 'N/A')
      );
    COLUMNS[Column.DSP_FEES].render = (_, { channel: rowChannel, rateSource }) => {
      if (isMeasurementChannel(rowChannel) || rateSource === 'PACKAGE' || rateSource === 'TECH_FEE') {
        return cellText('N/A');
      }
      // MANUAL / rate card: buy rate is DSP-inclusive — fee is not applied again in margin.
      if (rateSource === 'MANUAL' || rateSource == null) {
        return cellText(t('dspFeeIncludedShort'));
      }
      return cellText('N/A');
    };
    /* Commercial fields, edited in place. Each saves through the same PUT the
       form uses, so the server recalculates margins, the plan total and the
       bookings exactly as it would from the wizard — the cell decides nothing
       about pricing, it only carries the new value. */
    COLUMNS[Column.SELL_RATE].render = (_, row) => (
      <EditableCell
        value={row.sellRate}
        type="number"
        format={(v) => getCurrencyFormat(v as number, row.currency ?? planCurrency)}
        /* A package sets the rate, so editing it here would be overwritten by
           the package the next time the line is opened. */
        disabled={row.rateSource === 'PACKAGE'}
        disabledReason={t('lineItemFieldLocked')}
        onSave={(value) => saveField(row._id, 'sellRate', value)}
      />
    );
    COLUMNS[Column.START_DATE].render = (_, row) => (
      <EditableCell
        value={row.startDate ? dayjs(row.startDate).toISOString() : null}
        type="date"
        /* A flight cannot start after it ends. */
        maxDate={row.endDate ? dayjs(row.endDate).toISOString() : null}
        format={(v) => getDateFormat(v ? new Date(v as string) : undefined)}
        onSave={(value) => saveField(row._id, 'startDate', value)}
      />
    );
    COLUMNS[Column.END_DATE].render = (_, row) => (
      <EditableCell
        value={row.endDate ? dayjs(row.endDate).toISOString() : null}
        type="date"
        minDate={row.startDate ? dayjs(row.startDate).toISOString() : null}
        format={(v) => getDateFormat(v ? new Date(v as string) : undefined)}
        onSave={(value) => saveField(row._id, 'endDate', value)}
      />
    );
    /* Displays the resolved gross but edits the stored budget — the figure the
       line is actually priced from. Gross is derived from it by discount and
       value-add, so writing back the displayed number would compound them. */
    const resolveGross = (
      { budget, grossBudget, applyAgencyDiscount, valueAddType, valueAddInputMode, valueAddPercent, valueAddAbsolute, currency }:
        ChannelListItem
    ) => {
      const gross = resolveLineGrossBudget(budget, {
        applyAgencyDiscount,
        planAgencyDiscount: agencyDiscount,
        valueAddType,
        valueAddInputMode,
        valueAddPercent,
        valueAddAbsolute,
        storedGrossBudget: grossBudget
      });
      return gross != null ? getCurrencyFormat(gross, currency ?? planCurrency) : '—';
    };
    COLUMNS[Column.BUDGET].render = (_, row) => (
      <EditableCell
        value={row.budget}
        type="number"
        format={() => resolveGross(row)}
        onSave={(value) => saveField(row._id, 'budget', value)}
      />
    );
    COLUMNS[Column.NET_BUDGET].render = (
      _,
      { netBudget, budget, applyAgencyDiscount, valueAddType, valueAddInputMode, valueAddPercent, valueAddAbsolute, currency }
    ) => {
      const net = resolveLineNetBudget(budget, {
        applyAgencyDiscount,
        planAgencyDiscount: agencyDiscount,
        valueAddType,
        valueAddInputMode,
        valueAddPercent,
        valueAddAbsolute,
        storedNetBudget: netBudget
      });
      return cellText(
        net != null ? getCurrencyFormat(net, currency ?? planCurrency) : '—'
      );
    };
    COLUMNS[Column.VALUE].render = (_, { valueAddAbsolute, currency }) =>
      cellText(
        valueAddAbsolute != null && Number(valueAddAbsolute) > 0
          ? getCurrencyFormat(valueAddAbsolute, currency ?? planCurrency)
          : '—'
      );
    COLUMNS[Column.HARD_COST].render = (_, { hardCost, currency }) =>
      cellText(getCurrencyFormat(hardCost, currency ?? planCurrency));
    COLUMNS[Column.TRADING_DEAL_PERCENT].render = (_, { tradingDealPercent }) =>
      cellText(typeof tradingDealPercent === 'number' ? `${tradingDealPercent}%` : '0%');
    COLUMNS[Column.GROSS_MARGIN].render = (_, row) =>
      cellText(formatMarginPercent(resolveGrossMarginPercent(row)));
    COLUMNS[Column.EXPECTED_MARGIN].render = (_, row) => cellText(getPercentFormat(row.marginPercent));
    COLUMNS[Column.IMPRESSIONS].render = (_, row) => {
      const show = shouldShowImpressions(row.buyingMetric);
      return cellText(show ? getNumberFormat(row.impressions) : 'N/A');
    };
    COLUMNS[Column.ESTIMATED_CLICKS].render = (_, row) => {
      const show = shouldShowEstimatedClicks(row.buyingMetric, row.kpi);
      const value = show ? resolveDeliveredClicks(row) : undefined;
      return cellText(show ? getNumberFormat(value) : 'N/A');
    };
    COLUMNS[Column.ESTIMATED_VIEWS].render = (_, row) => {
      const show = shouldShowEstimatedViews(row.buyingMetric, row.kpi);
      const value = show ? resolveDeliveredViews(row) : undefined;
      return cellText(show ? getNumberFormat(value) : 'N/A');
    };
    COLUMNS[Column.ESTIMATED_LISTENS].render = (_, row) => {
      const show = shouldShowEstimatedListens(row.buyingMetric, row.kpi);
      return cellText(show ? getNumberFormat(row.listens) : 'N/A');
    };
    COLUMNS[Column.PO_NUMBER].render = (_, row) => (
      <EditableCell
        value={row.poNumber}
        type="text"
        onSave={(value) => saveField(row._id, 'poNumber', value)}
      />
    );
    COLUMNS[Column.TARGETING_DETAILS].render = (_, row) => (
      <EditableCell
        value={row.targetingDetails}
        type="text"
        onSave={(value) => saveField(row._id, 'targetingDetails', value)}
      />
    );
    COLUMNS[Column.DSP].render = (_, row) => (
      <EditableCell
        value={row.dsp}
        type="select"
        options={(dspOptionsData?.dsps ?? []).map((dsp) => ({
          label: dsp.dspName,
          value: dsp.dspName
        }))}
        /* A measurement line has no DSP, and a package or tech fee carries the
           one its pricing implies. An unset rateSource is manual — the DSP fee
           column beside this one reads it the same way, and treating null as
           non-manual disabled the dropdown on most lines. */
        disabled={
          isMeasurementChannel(row.channel) ||
          (row.rateSource != null && row.rateSource !== 'MANUAL')
        }
        disabledReason={t('lineItemFieldLocked')}
        onSave={(value) => saveField(row._id, 'dsp', value)}
      />
    );
    COLUMNS[Column.ACTIONS].render = (_, data) => (
      <Actions
        id={data._id}
        isBooked={isBooked}
        edit={() => openChannelForm(data)}
        duplicate={() => openChannelForm(duplicateLineItem(data))}
      />
    );

    return Object.values(COLUMNS).map(({ i18nKey, key, ...rest }) => ({
      title: t(i18nKey),
      key,
      ...rest
    }));
  }, [openChannelForm, t, planCurrency, agencyDiscount, isBooked, saveField, dspOptionsData]);

  const channelList: ChannelListItem[] = useMemo(
    () => channels?.data.map((channel) => ({ key: channel._id, ...channel })) ?? [],
    [channels]
  );

  return {
    columns,
    limit,
    page,
    count: channels?.count,
    data: channelList,
    isLoading,
    handlePaginationChange
  };
};
